// ...existing code...
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import { Notification } from '@weirgroup/weir-dls'
import { DisplayMap } from '../../components/map/map'
import { Filter } from './components/filter'
import { PopupModal } from '../../components/Modal/modal'
import settingsApi from '../../api/settings.api'
import { AppConfigContext } from '../../contexts/app-config.context'
import { TableView, TableViewRef } from './components/table-view'
import { getFilters } from '../../helpers/getFilters'
import { onFilterAssets } from '../../utils/on-filter-assets'

import { FetchedUserSettingType, RolesType } from '../../types/user.type'
import { AssetType, BucketDataType } from '../../types/bucketTypes'
import { FilterType, SelectedFilterType } from '../../types/filter.type'

import { useTranslation } from 'react-i18next'
import { groupReplacementParts } from '../../utils/replacement-parts.util'
import { getClientTimeZone } from '../../helpers/timezone.helper'
import {
    GUEST_POPUP,
    ROLES,
    StorageService,
} from '../../services/storage_service'
import { Roles } from '../../constants/roles.constants'
import { GuestPopup } from './components/guest-popup'
import { ToastDataType } from '../../types/toast.type'
import { fetchBucketList } from '../../utils/getTagData.utils'
import { useDarkMode } from '../../hooks/use-darkmode'
import { logError } from '../../services/logger.service'
import { searchAssets } from '../../helpers/search-assets'
import { useAnalyticsEvent } from '../../hooks/useAnalyticsEvent'
import { LoadingSpinner } from '../../components/loading-spinner'

// Zoom calculation constants
const ZOOM_THRESHOLD_VERY_CLOSE = 0.01
const ZOOM_THRESHOLD_CLOSE = 0.05
const ZOOM_THRESHOLD_NEARBY = 0.1
const ZOOM_THRESHOLD_REGIONAL = 0.5

const ZOOM_LEVEL_VERY_CLOSE = 12
const ZOOM_LEVEL_CLOSE = 10
const ZOOM_LEVEL_NEARBY = 8
const ZOOM_LEVEL_REGIONAL = 6
const ZOOM_LEVEL_WIDE = 4
const SINGLE_ASSET_ZOOM = 15
const ZOOM_LEVEL_DEFAULT = 5

// Zoom level thresholds for smart zoom calculation
const ZOOM_LEVELS = [
    { threshold: ZOOM_THRESHOLD_VERY_CLOSE, zoom: ZOOM_LEVEL_VERY_CLOSE },
    { threshold: ZOOM_THRESHOLD_CLOSE, zoom: ZOOM_LEVEL_CLOSE },
    { threshold: ZOOM_THRESHOLD_NEARBY, zoom: ZOOM_LEVEL_NEARBY },
    { threshold: ZOOM_THRESHOLD_REGIONAL, zoom: ZOOM_LEVEL_REGIONAL },
];

interface Props {
    selectedAsset: any
    setSelectedAsset: Dispatch<SetStateAction<any>>
}
type Coordinates = { lat: number; lng: number }
export const MapPage = (props: Props) => {
    const { t } = useTranslation()
    const logAnalyticsEvent = useAnalyticsEvent();
    const { selectedAsset, setSelectedAsset } = props
    const appConfigStore = useContext(AppConfigContext)
    const [assetData, setAssetData] = useState<AssetType[]>([])
    const [filteredAssetData, setFilteredAssetData] = useState<any>([])
    const [filteredBucketList, setFilteredBucketList] = useState<any>([])
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [filters, setFilters] = useState<FilterType[]>([])
    const [tagData, setTagData] = useState<any>()
    const [replacementParts, setReplacementParts] = useState<any>()
    const [isModalShown, setIsModalShown] = useState(false)
    const [isTableShown, setIsTableShown] = useState<boolean>(false)
    const [isFetching, setIsFetching] = useState<boolean>(false)
    const [isRefetching, setIsRefetching] = useState<boolean>(false)
    const [isGuest, setIsGuest] = useState<boolean>(false)
    const [isGuestPopupShown, setIsGuestPopupShown] = useState<boolean>()
    const [isFetchingGeofences, setIsFetchingGeofences] =
        useState<boolean>(false)
    const [showToast, setShowToast] = useState<boolean>(false)
    const [toastData, setToastData] = useState<any>()
    const [searchMapCenter, setSearchMapCenter] = useState<Coordinates | null>(null)
    const [searchMapZoom, setSearchMapZoom] = useState<number>(ZOOM_LEVEL_DEFAULT)
    // Track status for each asset by serial_number
    const [assetStatuses] = useState<{ [serialNumber: string]: string }>({});
    const [assetStatus, setAssetStatus] = useState<string>("")
    const tableViewRef = useRef<TableViewRef>(null)

    const roleData = StorageService.getItem(ROLES)
    const guestPopupStorageData: string | null =
        StorageService.getItem(GUEST_POPUP)

    const {darkMode} = useDarkMode()

    useEffect(() => {
        if (isGuest) {
            if (guestPopupStorageData) {
                let shown = guestPopupStorageData === 'true'
                setIsGuestPopupShown(shown)
            } else {
                StorageService.setItem(GUEST_POPUP, false)
            }
        }
    }, [guestPopupStorageData, isGuest])

    useEffect(() => {
        if (roleData) {
            const roles: RolesType = JSON.parse(roleData)
            setIsGuest(roles?.role === Roles.GUEST)
        }
    }, [roleData])
    const fetchBucketListFunc = async () => {
        // I created a fetchBucketListFunc function and incorporated the fetchBucketList global function within it
        setIsFetching(true)
        fetchBucketList()
            .then((data) => {
                setAssetData(data.fetchedAssets)
                // Removed console.log for production
                if (data.fetchedAssets) {
                    const buckets = extractBucketList(data.fetchedAssets)
                    setFilteredBucketList(buckets)
                    setSelectedAsset(data.fetchedAssets)
                    if (data.refetch) {
                        setIsRefetching(data.refetch)
                    }
                }
                setIsFetching(false)
            })
            .catch((error: any) => {
                logError('Failed to fetch bucket list', error, 'MapPage.fetchBucketListFunc');
                setIsFetching(false)
            })
    }
    useEffect(() => {
        if (isRefetching) {
            const updatedSelectedAsset = filteredBucketList.filter(
                (bucket: BucketDataType) => {
                    return bucket.asset_id === selectedAsset.asset_id
                }
            )
            setSelectedAsset(updatedSelectedAsset[0])
            setIsRefetching(false)
        }
    }, [selectedAsset, isRefetching])
    /**
     * Fetch user settings from the API
     */
    const fetchUserSettings = async (): Promise<void> => {
        try {
            const fetchedData = await settingsApi.getUserSettings()
            const userSettings: FetchedUserSettingType = fetchedData.data.data
            if (userSettings) {
                appConfigStore.setTimezone(
                    userSettings.timezone ?? getClientTimeZone()
                )
                appConfigStore.setLanguage(userSettings.language)
                appConfigStore.setUnits(userSettings.units)
                appConfigStore.setAssetEmailNotification(
                    userSettings.asset_email_notification
                )
                appConfigStore.setGeofenceEmailNotification(
                    userSettings.geofence_email_notification
                )
                appConfigStore.setInAppNotification(userSettings.inapp_notification)
            }
        } catch (error) {
            logError('Failed to fetch user settings', error as Error, 'MapPage.fetchUserSettings');
        }
    }

    /**
     * Extract bucket list from asset data
     * @param assetData - Array of asset data
     * @returns Array of bucket data with tag IDs
     */
    const extractBucketList = (assetData: AssetType[]): BucketDataType[] => {
        let buckets: BucketDataType[] = []
        assetData.forEach((asset: AssetType) => {
            let esnTagId = asset.tag?.esn
            let bucket = asset.bucket
            bucket.tag_id = esnTagId
            bucket.asset_status = asset.asset_status ?? ""
            let data = bucket
            buckets.push(data)
        })
        return buckets
    }

    /**
     * Apply selected filters to the asset list
     * @param filteredAssets - Array of assets to filter
     * @returns Filtered array of assets
     */
    const applyFilters = (filteredAssets: AssetType[]) => {
        if (!filters || filters.length === 0) {
            return filteredAssets;
        }
    
        const selectedFilters: SelectedFilterType[] = [];
        filters.forEach((filter) => {
            filter.type.forEach((item) => {
                if (item.checked) {
                    const selected = {
                        category: filter.category.toLowerCase(),
                        title: item.originalTitle || item.title,
                        id: item.id
                         // Use originalTitle for filtering
                    };
                    selectedFilters.push(selected);
                }
            });
        });
        
        return onFilterAssets(selectedFilters, filteredAssets);
    };

    /**
     * Filter assets based on search query and selected filters
     */
    const filterAssets = () => {
        if (!filters) {
            return;
        }
    
        if (!assetData) {
            return;
        }
        
        const filteredAssets = applyFilters(assetData);
        const searchedAssets = searchAssets(filteredAssets, searchQuery);

        setFilteredAssetData(searchedAssets);
        setFilteredBucketList(extractBucketList(searchedAssets));

        tableViewRef.current?.resetTablePage();
        
        // Smart zoom based on search results
        if (searchQuery && searchedAssets.length > 0) {
            if (searchedAssets.length === 1) {
                // Single asset - zoom in close
                centerMapOnSingleAsset(searchedAssets[0]);
            } else {
                // Multiple assets - zoom out to fit all
                fitAllAssetsOnMap(searchedAssets);
            }
            logAnalyticsEvent('web_user_searched_assets', { description: 'User searched for assets on map', searchQuery, resultCount: searchedAssets.length });
        } else if (!searchQuery) {
            // Reset zoom when search is cleared
            resetSearchMapState();
        }
    };

    /**
     * Get tag data for a selected asset
     * @param selectedAsset - The asset to get tag data for
     * @returns Tag data or null if not found
     */
    const getTagData = (selectedAsset: any) => {
        const data: AssetType[] = assetData.filter((asset: AssetType) => {
            // Optionally update assetStatus for backward compatibility
            setAssetStatus(asset.asset_status ?? "")
            if (asset.asset_id === selectedAsset.asset_id) {
                return asset.tag
            }
        })
        return data.length > 0 ? data?.[0]?.tag : null
    }

    // Example: Expose setAssetStatuses to children or wherever you handle uploads
    // You should call setAssetStatuses({ ... }) as described in the previous message when uploading assets

    useEffect(() => {
        if (!isModalShown) {
            setSelectedAsset(null)
            setTagData(null)
        }
    }, [isModalShown])

    useEffect(() => {
        if (selectedAsset) {
            setReplacementParts(
                groupReplacementParts(selectedAsset.replacement_parts)
            )
            let tagInfo = getTagData(selectedAsset)
            setTagData(tagInfo)
        }
    }, [selectedAsset])

    useEffect(() => {
        setFilters(getFilters(assetData))
    }, [assetData])

    // Auto-filter when filters change (from checkbox toggles)
    useEffect(() => {
        if (filters.length > 0) {
            filterAssets();
        }
    }, [filters])

    useEffect(() => {
        const initializeComponent = async () => {
            try {
                await fetchBucketListFunc();
                await fetchUserSettings();
                logAnalyticsEvent('web_user_viewed_map_page', { description: 'User viewed map page' });
            } catch (error) {
                logError('Error initializing component', error as Error, 'MapPage.initializeComponent');
            }
        };
        
        initializeComponent();
    }, [])

    // Track view changes in page title for analytics
    useEffect(() => {
        const viewSuffix = isTableShown ? ' - Table View' : ' - Map View';
        const currentTitle = document.title;
        const baseTitle = currentTitle.replace(/ - (Map|Table) View$/, '');
        document.title = baseTitle + viewSuffix;
    }, [isTableShown])

    /**
     * Trigger toast message for user feedback
     * @param success - Whether the operation was successful
     * @param result - Result object containing message data
     */
    const triggerToastMessage = (success: boolean, result?: any): void => {
        const toastData: ToastDataType = {
            message: success ? result.data : result.response.data.message,
            variant: success ? 'success' : 'critical',
        }
        setShowToast(true)
        setToastData(toastData)
    }

    /**
     * Helper function to fit all assets on map with appropriate zoom.
     * @param assets - Array of AssetType objects to fit on the map.
     */
        const resetSearchMapState = useCallback(() => {
        setSearchMapCenter(null);
        setSearchMapZoom(ZOOM_LEVEL_DEFAULT); // Reset to default zoom
    }, []);

    /**
     * Handle Add Asset button click - navigate to add bulk asset page
     */

    // Helper function to center map on single asset with high zoom
    const centerMapOnSingleAsset = (asset: AssetType) => {
        const location = asset.tag?.location
        if (location?.latitude && location?.longitude) {
            setSearchMapCenter({
                lat: location.latitude,
                lng: location.longitude
            })
            setSearchMapZoom(SINGLE_ASSET_ZOOM) // High zoom for single asset
        }
    }

    // Helper function to fit all assets on map with appropriate zoom
    const fitAllAssetsOnMap = (assets: AssetType[]) => {
        // Get all valid locations
        const locations = assets
            .map(asset => asset.tag?.location)
            .filter(location => location?.latitude && location?.longitude)
            .map(location => ({
                lat: location.latitude,
                lng: location.longitude
            }))

        if (locations.length === 0) return

        if (locations.length === 1) {
            // Fallback to single asset if only one valid location
            setSearchMapCenter(locations[0])
            setSearchMapZoom(ZOOM_LEVEL_VERY_CLOSE)
            return
        }

        // Calculate bounds
        const latitudes = locations.map(loc => loc.lat)
        const longitudes = locations.map(loc => loc.lng)
        
        const maxLat = Math.max(...latitudes)
        const minLat = Math.min(...latitudes)
        const maxLng = Math.max(...longitudes)
        const minLng = Math.min(...longitudes)

        // Calculate center point
        const centerLat = (maxLat + minLat) / 2
        const centerLng = (maxLng + minLng) / 2

        setSearchMapCenter({
            lat: centerLat,
            lng: centerLng
        })

        // Calculate appropriate zoom level based on bounds
        const latDiff = maxLat - minLat
        const lngDiff = maxLng - minLng
        const maxDiff = Math.max(latDiff, lngDiff)

        // Smart zoom calculation (adjust these values as needed)
        let zoom = ZOOM_LEVEL_WIDE // Default to widest zoom
        for (const entry of ZOOM_LEVELS) {
            if (maxDiff < entry.threshold) {
                zoom = entry.zoom
                break
            }
        }

        setSearchMapZoom(zoom)
    }

    const tableToggleOnChange = () => {
        const newViewState = !isTableShown;
        logAnalyticsEvent('web_user_toggled_view', { description: 'User toggled view', viewType: newViewState ? 'table' : 'map' });
        setIsTableShown(newViewState)
    }
    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 5000)
        }
    }, [toastData])

    return (
        <>
            <div className={"map-page-container"} style={{backgroundColor:darkMode ? '#3B434C' : '#F6F7F8'}}>
                <div className='filter-container' >
                    <Filter
                        tableToggleOnChange={tableToggleOnChange}
                        isTableShown={isTableShown}
                        filterAssets={filterAssets}
                        setSearchQuery={setSearchQuery}
                        filters={filters}
                        setFilters={setFilters}
                    />
                </div>

                {isFetching ? (
                <div className='map-table-view-container'>
                    <LoadingSpinner darkMode={darkMode} message={t('fetchingAssets').toString()} />
                    </div>
                ) : (
                    <div className='map-table-view-container'>
                    {isTableShown ? (
                        <TableView
                            bucketList={filteredBucketList}
                            setSelectedAsset={setSelectedAsset}
                            getTagData={getTagData}
                            setTagData={setTagData}
                            isFetchingGeofences={isFetchingGeofences}
                            ref={tableViewRef}
                        />
                    ) : (
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                            <DisplayMap
                                bucketList={filteredAssetData}
                                setIsModalShown={setIsModalShown}
                                isModalShown={isModalShown}
                                setSelectedAsset={setSelectedAsset}
                                getTagData={getTagData}
                                setTagData={setTagData}
                                searchCenter={searchMapCenter}
                                zoom={searchMapZoom}
                            />
                        </div>
                    )}
                    </div>

                )}
            </div>
            {isModalShown ? (
                <PopupModal
                    selectedAsset={selectedAsset}
                    setSelectedAsset={setSelectedAsset}
                    isModalShown={isModalShown}
                    setIsModalShown={setIsModalShown}
                    tagData={tagData}
                    fetchBucketList={fetchBucketListFunc}
                    replacementParts={replacementParts}
                    isGuest={isGuest}
                    isFetchingGeofences={isFetchingGeofences}
                    setIsFetchingGeofences={setIsFetchingGeofences}
                    triggerToastMessage={triggerToastMessage}
                />
            ) : (
                <></>
            )}
            {!isGuestPopupShown && isGuest ? (
                <GuestPopup isGuestPopupShown={isGuestPopupShown} />
            ) : (
                <></>
            )}
            {showToast ? (
                <div className="toast-container">
                    <Notification 
                        title={toastData.variant === "success" ? "Success" : "Fail"}
                        textContent={toastData.message}
                        variant={toastData.variant}
                        onClose={()=>{setShowToast(false)}}
                    />
                </div>
            ) : (
                <></>
            )}
        </>
    )
}

export default MapPage
